import React, { useState, useEffect } from 'react';
import { Loader, AlertCircle, RefreshCw } from 'lucide-react';
import { OrderTimelineDto } from '../../../../types/orderTimeline';
import orderTimelineService from '../../../../services/orderTimelineService';
import { TimelineHeader } from './components/TimelineHeader';
import { TimelineSummary } from './components/TimelineSummary';
import { TimelineProblems } from './components/TimelineProblems';
import { TimelineEventList } from './components/TimelineEventList';
import { TimelineOrderLines } from './components/TimelineOrderLines';
import { TimelinePickingLists } from './components/TimelinePickingLists';
import { TimelineStockTransactions } from './components/TimelineStockTransactions';
import { TimelineStockStatus } from './components/TimelineStockStatus';
import { TimelineReservations } from './components/TimelineReservations';
import { TimelineLogEntries } from './components/TimelineLogEntries';
import { useTranslation } from '../../../../contexts/I18nProvider';

interface OrderTimelineContentProps {
  orderNo: number;
}

export const OrderTimelineContent: React.FC<OrderTimelineContentProps> = ({ orderNo }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<OrderTimelineDto | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchTimeline = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await orderTimelineService.getOrderTimeline(orderNo);
      setData(result);
    } catch (err: any) {
      setError(err.message || t('manager.modules.orderTimeline.errors.loadFailed', {}, 'Kunne ikke hente ordretidslinje'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (orderNo > 0) {
      fetchTimeline();
    }
  }, [orderNo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader className="w-6 h-6 text-blue-500 animate-spin" />
        <span className="ml-3 text-gray-500 text-sm">{t('manager.modules.orderTimeline.loadingForOrder', { '0': orderNo }, `Henter ordretidslinje for ordre ${orderNo}...`)}</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <AlertCircle className="w-8 h-8 text-red-400 mb-2" />
        <p className="text-red-600 text-sm font-medium">{error}</p>
        <button
          onClick={fetchTimeline}
          className="mt-3 flex items-center gap-2 px-3 py-1.5 text-sm text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
        >
          <RefreshCw className="w-4 h-4" />
          {t('manager.modules.orderTimeline.retry', {}, 'Prøv igjen')}
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400 text-sm">
        {t('common.messages.noData', {}, 'Ingen data tilgjengelig')}
      </div>
    );
  }

  return (
    <div className="space-y-0">
      {/* Refresh button */}
      <div className="flex justify-end mb-2">
        <button
          onClick={fetchTimeline}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <RefreshCw className="w-3.5 h-3.5" />
          {t('common.buttons.refresh', {}, 'Oppdater')}
        </button>
      </div>

      {/* Order header */}
      <TimelineHeader data={data} />

      {/* Summary stats */}
      <TimelineSummary summary={data.summary} />

      {/* Problems (shown prominently if any) */}
      <TimelineProblems problems={data.problems} />

      {/* Order lines */}
      <TimelineOrderLines lines={data.orderLines} />

      {/* Picking lists */}
      <TimelinePickingLists pickingLists={data.pickingLists} />

      {/* Stock transactions */}
      <TimelineStockTransactions transactions={data.stockTransactions} />

      {/* Current stock status */}
      <TimelineStockStatus stockStatus={data.currentStockStatus} />

      {/* Reservations */}
      <TimelineReservations reservations={data.reservations} />

      {/* Chronological timeline */}
      <TimelineEventList events={data.timelineEvents} />

      {/* Log entries (loaded on-demand) */}
      <TimelineLogEntries orderNo={orderNo} />
    </div>
  );
};
